import { Fragment, useContext, useEffect, useState } from "react";
import { Autocomplete, Box, CircularProgress, Divider, Grid, InputAdornment, TextField, createTheme, Avatar, Checkbox, Chip } from "@mui/material";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../../routing/LangRouter";

export default function AutoCompleteComponent({
    dir,
    width,
    label,
    icon,
    type,
    index,
    nestIndex,
    options = [],
    name,
    defaultValue,
    handleChange,
    setInputValue,
    open,
    focused,
    onFocus,
    innerRef,
    loading,
    disabled,
    control,
    register,
    errors,
    showImages = false, // New prop to enable/disable image rendering
    imageField = "image", // Field name for image URL
    imageFallback = null, // Fallback image or icon
    hasMore,
    onLoadMore,
    multiple = false,
    enableSelectAll = false,
    selectAllLabel = "انتخاب همه",
}) {
    const theme = createTheme({
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 900,
                lg: 1200,
                xl: 1536,
            },
        },
    });

    const { t } = useTranslation();
    const { locale } = useContext(LocaleContext);
    const [isOpen, setIsOpen] = useState(false);
    const [shrink, setShrink] = useState(!!defaultValue || !!focused);

    useEffect(() => {
        setShrink(!!defaultValue || !!focused);
    }, [defaultValue, focused]);

    const getOptionLabel = option => {
        if (locale === "en") return option?.english_name || option?.name;
        return option?.name || option?.english_name || option?.label || "";
    };

    const renderOption = option => {
        const name =
            locale === "en"
                ? option?.english_name || option?.name || option?.label || ""
                : option?.name || option?.english_name || option?.label || "";

        const locationDetails = type !== "other" && (
            <span>
                {`${option?.city ? (locale === "en" ? option?.english_city + " - " : option?.city + " - ") : ""}${
                    locale === "en" ? option?.country?.english_name || option?.english_country || "" : option?.country?.name || option?.country || ""
                }${option?.code ? " - " + option?.code : ""}`}
            </span>
        );

        // Image rendering logic
        const renderImage = () => {
            if (!showImages) return null;

            const imageUrl = option?.[imageField] || option?.image || option?.avatar || option?.icon;

            if (imageUrl) {
                return (
                    <Avatar
                        src={imageUrl}
                        alt={name}
                        sx={{
                            width: 32,
                            height: 32,
                            marginRight: 1,
                            flexShrink: 0,
                        }}
                    >
                        {/* Fallback to first letter of name if image fails to load */}
                        {name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                );
            } else if (imageFallback) {
                return (
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            marginRight: 1,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {imageFallback}
                    </Box>
                );
            }

            return null;
        };

        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexGrow: 1,
                    padding: ".25rem 0",
                    "& span": {
                        color: "var(--gray-color)",
                    },
                }}
            >
                {renderImage()}
                <Box sx={{ flexGrow: 1 }}>
                    {name}
                    <br />
                    {locationDetails}
                </Box>
            </Box>
        );
    };

    const getTextFieldProps = (params, value, fieldState) => ({
        ...params,
        dir,
        fullWidth: true,
        label,
        inputRef: innerRef,
        error: !!errors || !!fieldState.error,
        helperText: errors || fieldState.error?.message,
        focused,
        onFocus: () => {
            setShrink(onFocus || true);
            setIsOpen(true);
        },
        onBlur: e => {
            setShrink(!!e.target.value);
        },
        InputLabelProps: { shrink: shrink || !!value },
        InputProps: {
            ...params.InputProps,
            startAdornment: (
                <>
                    <InputAdornment position="start">{icon}</InputAdornment>
                    {params.InputProps.startAdornment}
                </>
            ),
        },
    });

    //styles
    const popperStyles = {
        "&.MuiAutocomplete-popper": {
            [theme.breakpoints.between("xs", "md")]: {
                width: open ? "100% !important" : "350 !important",
                borderRadius: "0",
                transition: open ? "transform 0.8s ease-in-out" : "none",
            },
        },
    };

    const paperStyles = {
        "&.MuiAutocomplete-paper": {
            [theme.breakpoints.between("xs", "md")]: {
                width: "100%",
                height: open ? "100vh" : "auto",
                borderRadius: "0",
            },
            [theme.breakpoints.up("md")]: {
                width: width,
            },
            "& .MuiAutocomplete-listbox": {
                [theme.breakpoints.between("xs", "md")]: {
                    height: open ? "90vh" : "auto",
                    maxHeight: open ? "90vh !important" : "auto",
                },
                // Enhanced styles for options with images
                "& .MuiAutocomplete-option": {
                    minHeight: showImages ? "48px" : "auto",
                    padding: showImages ? "8px 16px" : "6px 16px",
                },
            },
        },
    };

    const styles = {
        width: "100%",
        height: "3rem",
        display: "inline-block",
        backgroundColor: disabled ? "var(--white-gray-color) !important" : "var(--light-color)",
        borderRadius: "10px",
        [theme.breakpoints.down("md")]: {
            height: "2.75rem",
        },
        "& .MuiFormControl-root": {
            height: "100%",
        },
        "& .MuiAutocomplete-inputRoot": {
            height: "100%",
            borderRadius: "10px",
            alignItems: "center",
            "& fieldset": {
                border: "0.5px solid var(--medium-gray-color)",
            },
            "&:hover fieldset, &.Mui-focused fieldset": {
                border: "0.5px solid var(--medium-gray-color)",
            },
        },
        "& label": {
            color: "var(--teal-color-18)",
            fontSize: "0.875rem",
            marginLeft: icon && !shrink ? "1rem" : "0",
            [theme.breakpoints.between("xs", "md")]: {
                fontSize: "0.625rem",
            },
            "&.Mui-focused": {
                color: "var(--primary-color)",
                marginLeft: "0",
            },
        },
        "& input": {
            fontSize: "0.875rem",
            [theme.breakpoints.down("md")]: {
                fontSize: "0.75rem",
            },
        },
        "& .MuiFormHelperText-contained": {
            [theme.breakpoints.down("md")]: {
                fontSize: "0.625rem",
            },
        },
    };

    //handle change
    const handleAutocompleteChange = (event, newValue, onChange, currentValue = []) => {
        let updatedValue = newValue;

        if (multiple && enableSelectAll) {
            const clickedSelectAll = newValue?.some(option => option?.id === SELECT_ALL_OPTION.id);

            if (clickedSelectAll) {
                const allSelected = options.length > 0 && currentValue?.length === options.length;
                updatedValue = allSelected ? [] : options;
            } else {
                updatedValue = newValue?.filter(option => option?.id !== SELECT_ALL_OPTION.id) || [];
            }
        }

        onChange?.(updatedValue);
        setShrink(!!newValue);
        !register && handleChange?.(updatedValue, name, index, nestIndex);
    };

    const loadingIndicator = (
        <Grid container item xs={12} justifyContent="center">
            <CircularProgress color="inherit" size={20} />
        </Grid>
    );

    const SELECT_ALL_OPTION = {
        id: "__select_all__",
        name: selectAllLabel,
        english_name: selectAllLabel,
        label: selectAllLabel,
        isSelectAll: true,
    };

    const displayedOptions = multiple && enableSelectAll ? [SELECT_ALL_OPTION, ...options] : options;

    return (
        <Controller
            control={control}
            name={name}
            rules={register || ""}
            render={({ field: { onChange, value }, fieldState }) => (
                <Autocomplete
                    multiple={multiple}
                    disableCloseOnSelect={multiple}
                    options={displayedOptions}
                    value={multiple ? defaultValue || value || [] : defaultValue || value || null}
                    open={open || isOpen}
                    onOpen={() => {
                        setIsOpen(true);
                    }}
                    onClose={() => {
                        setIsOpen(false);
                    }}
                    onChange={(event, newValue) => handleAutocompleteChange(event, newValue, onChange, multiple ? defaultValue || value || [] : [])}
                    onInputChange={(event, newInputValue) => setInputValue?.(newInputValue)}
                    renderTags={(tagValue, getTagProps) => {
                        if (!multiple) return null;

                        const allSelected = options.length > 0 && tagValue.length === options.length;

                        if (allSelected) {
                            return (
                                <Box>
                                    <Chip label={selectAllLabel} size="small" {...getTagProps({ index: 0 })} />
                                </Box>
                            );
                        }

                        if (tagValue.length > 0) {
                            return (
                                <Box>
                                    <Chip
                                        label={`${tagValue.length} ${locale === "en" ? "selected" : "انتخاب شده "}`}
                                        size="small"
                                        {...getTagProps({ index: 0 })}
                                    />
                                </Box>
                            );
                        }

                        return null;
                    }}
                    renderInput={params => <TextField {...getTextFieldProps(params, multiple ? defaultValue || value || [] : value, fieldState)} />}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    getOptionLabel={getOptionLabel}
                    renderOption={(props, option, { selected, index }) => {
                        const { key, ...optionProps } = props;
                        const isLastOption = index === options.length - 1;
                        const allSelected = multiple && options.length > 0 && (defaultValue || value || []).length === options.length;
                        return (
                            <Fragment key={option.id}>
                                <li {...optionProps}>
                                    {multiple && <Checkbox checked={option?.isSelectAll ? allSelected : selected} />}
                                    {option?.isSelectAll ? <Box sx={{ fontWeight: 600 }}>{selectAllLabel}</Box> : renderOption(option)}
                                </li>
                                <Divider width="100%" />
                                {isLastOption && loading && hasMore && <li>{loadingIndicator}</li>}
                            </Fragment>
                        );
                    }}
                    filterOptions={(options, { inputValue }) => {
                        const filterValue = inputValue?.toLowerCase();
                        const searchableFields = [
                            "name",
                            "english_name",
                            "city",
                            "english_city",
                            "iata",
                            "code",
                            "city",
                            "english_city",
                            "country",
                            "english_country",
                        ];

                        return options?.filter(option =>
                            searchableFields?.some(field => {
                                const value = option?.[field];
                                return value ? value?.toLowerCase?.()?.includes?.(filterValue) : false;
                            }),
                        );
                    }}
                    groupBy={option => (option?.isSelectAll ? "" : option?.header)}
                    openOnFocus
                    autoHighlight
                    popupIcon={null}
                    loading={loading}
                    loadingText={loadingIndicator}
                    noOptionsText={t("noOptionText")}
                    disabled={disabled}
                    disableClearable={!multiple}
                    componentsProps={{
                        popper: { sx: popperStyles },
                        paper: { sx: paperStyles },
                    }}
                    sx={styles}
                    ListboxProps={{
                        onScroll: event => {
                            const listboxNode = event.currentTarget;
                            if (!loading && hasMore && listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50) {
                                onLoadMore?.();
                            }
                        },
                    }}
                />
            )}
        />
    );
}
